import React, { useState, useEffect, useMemo } from "react";
import SchedulingAssistentFilter from "./components/Filters";
import { cacheSideBarActive } from "utils";
import AssistantDetails from "./components/AssistantDetails";
import styles from "../AddEvent.module.scss";
import { useTranslation } from "react-i18next";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import constants from "../../../../constants";

const schduleViews = [
  { id: "4", name: "Daily" },
  { id: "5", name: "Weekly" },
  { id: "3", name: "Monthly" },
  // { id: "1", name: "Daily List" },
  { id: "2", name: "Weekly List" },
];

const VIEW_OPTION_WHITELIST = {
  1: ["officeName", "timeString"],
  2: ["tag", "title", "officeName", "timeString"],
  3: ["title", "officeName", "timeString", "staffMembers"],
  4: ["title", "officeName", "timeString", "staffMembers"],
  5: ["title", "officeName", "timeString", "staffMembers"],
};

/**
 * Keeps view-setting options in one place so each view maps to explicit allowed fields.
 */
const getViewSettingOptionsByView = (viewId, options) => {
  const allowedTypes = VIEW_OPTION_WHITELIST[viewId] || [];
  return options.filter((opt) => allowedTypes.includes(opt.type));
};

export default function SchedulingAssistantDetails({
  eventDate,
  setEventDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  isAllDayEvent,
  errors,
  eventOrShiftTab,
  selectedStaff,
  setSelectedStaff,
  ownerId,
  manageTab,
  eventSchedulingTabStatus,
  options,
  selectedOption,
  selectOption,
  today,
  setRepeatTypeValues,
  repeatMessagesDisplay,
  disabledDates,
  prevSelectedValues,
  ...rest
}) {
  const { t } = useTranslation();

  // Persisted view selection
  const persistedView =
    localStorage.getItem("assistantViewSelected") || schduleViews[0].id;
  const [assistantViewSelected, setAssistantViewSelected] =
    useState(persistedView);

  const filterEventsData = useMemo(
    () => [
      { title: t("accountOwner.eventCreatedByYou"), type: 0 },
      { title: t("accountOwner.eventCreatedByOthers"), type: -1 },
      { title: t("accountOwner.eventsNotAccepted"), type: 4 },
      { title: t("accountOwner.shiftCreatedByYou"), type: 8 },
      { title: t("accountOwner.shiftCreatedByOthers"), type: 6 },
      { title: t("accountOwner.shiftNotAccepted"), type: 7 },
      { title: t("accountOwner.busy"), type: 3 },
      { title: t("accountOwner.leaveApproved"), type: 2 },
      { title: t("accountOwner.leavePending"), type: 9 }, // type 9 will be matched with 1 in leave statusId
      { title: t("scheduler.agenda.appointmentTitle"), type: 1 },
    ],
    [t],
  );

  const assistantSettingViewOptions = useMemo(
    () => [
      { title: t("Tag"), type: "tag" },
      { title: t("title"), type: "title" },
      { title: t("waitingList.clinicName"), type: "officeName" },
      { title: t("dateAndTime"), type: "timeString" },
      { title: t("Staff Members"), type: "staffMembers" },
    ],
    [t],
  );

  const [viewSettingFilterOptions, setViewSettingFilterOptions] = useState(
    assistantSettingViewOptions,
  );
  const [isSidebarActive, setSidebarActive] = useState(cacheSideBarActive());
  const [agendaTypes, setAgendaTypes] = useState([]); // event type filters
  const [viewSettig, setViewSetting] = useState([]); // display setting filters
  const [agendaFilter, setAgendaFilter] = useState([]);
  const [settingFilter, setSettingFilter] = useState([]);
  const [agenda, setAgenda] = useState(null);
  const [agendaFilteredData, setAgendaFilteredData] = useState(null);
  const [localSelectedStaff, setLocalSelectedStaff] = useState(selectedStaff);
  const [showFreeSlots, setShowFreeSlots] = useState(false);
  const [showFindAvailabilityButtons, setShowFindAvailabilityButtons] =
    useState(false);
  const [checkFreeSlotAfterFilterApply, setCheckFreeSlotAfterFilterApply] =
    useState(false);

  // Sync localSelectedStaff with parent selectedStaff when selectedStaff changes
  useEffect(() => {
    setLocalSelectedStaff(selectedStaff);
  }, [selectedStaff]);

  useEffect(() => {
    setViewSettingFilterOptions(
      getViewSettingOptionsByView(
        assistantViewSelected,
        assistantSettingViewOptions,
      ),
    );
  }, [assistantViewSelected, assistantSettingViewOptions]);

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
    localStorage.setItem("isSidebarActive", !isSidebarActive);
  };
  const { data: officeData } = useAllActiveOffices(
    1,
    constants.ACTIVE_OFFICES_PAGE_SIZE,
    ownerId,
  );
  const canShowOfficeName = useMemo(
    () => officeData?.data?.length > 1,
    [officeData?.data],
  );

  const handleAssistantViewChange = (value) => {
    setAssistantViewSelected(value);
    localStorage.setItem("assistantViewSelected", value);
    setSettingFilter(() => {
      if (value !== "3" && value !== "4" && value !== "5") {
        return viewSettig.filter((type) => type !== "staffMembers");
      }
      return viewSettig;
    });
  };

  return (
    <div
      className={`${styles["filter_assistant_container"]} ${styles["right-side-bar-active"]}`}
    >
      <SchedulingAssistentFilter
        isSidebarActive={isSidebarActive}
        handleSidebarToggle={handleSidebarToggle}
        eventDate={eventDate}
        setEventDate={setEventDate}
        ownerId={ownerId}
        eventOrShiftTab={eventOrShiftTab}
        selectedStaff={localSelectedStaff}
        setSelectedStaff={setLocalSelectedStaff}
        onApplyStaff={() => setSelectedStaff(localSelectedStaff)}
        filterEventsData={filterEventsData}
        agendaTypes={agendaTypes}
        setAgendaTypes={setAgendaTypes}
        viewSettingFilterOptions={viewSettingFilterOptions}
        viewSettig={viewSettig}
        setViewSetting={setViewSetting}
        setAgendaFilter={setAgendaFilter}
        setSettingFilter={setSettingFilter}
        assistantViewSelected={assistantViewSelected}
        agenda={agenda}
        setAgendaFilteredData={setAgendaFilteredData}
        showFreeSlots={showFreeSlots}
        setShowFreeSlots={setShowFreeSlots}
        setShowFindAvailabilityButtons={setShowFindAvailabilityButtons}
        setCheckFreeSlotAfterFilterApply={setCheckFreeSlotAfterFilterApply}
      />
      <div className={styles.right_side_bar}>
        <AssistantDetails
          eventDate={eventDate}
          setEventDate={setEventDate}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          errors={errors}
          selectedStaff={selectedStaff}
          disabledDates={disabledDates}
          agendaTypes={agendaTypes}
          agendaFilter={agendaFilter}
          setAgendaFilter={setAgendaFilter}
          settingFilter={settingFilter}
          options={options}
          selectedOption={selectedOption}
          selectOption={selectOption}
          today={today}
          repeatMessagesDisplay={repeatMessagesDisplay}
          assistantViewSelected={assistantViewSelected}
          setRepeatTypeValues={setRepeatTypeValues}
          prevSelectedValues={prevSelectedValues}
          setAssistantViewSelected={handleAssistantViewChange}
          schduleViews={schduleViews}
          setAgenda={setAgenda}
          agendaFilteredData={agendaFilteredData}
          showFreeSlots={showFreeSlots}
          setShowFreeSlots={setShowFreeSlots}
          eventOrShiftTab={eventOrShiftTab}
          manageTab={manageTab}
          eventSchedulingTabStatus={eventSchedulingTabStatus}
          setShowFindAvailabilityButtons={setShowFindAvailabilityButtons}
          showFindAvailabilityButtons={showFindAvailabilityButtons}
          checkFreeSlotAfterFilterApply={checkFreeSlotAfterFilterApply}
          setCheckFreeSlotAfterFilterApply={setCheckFreeSlotAfterFilterApply}
          {...rest}
          canShowOfficeName={canShowOfficeName}
        />
      </div>
    </div>
  );
}
